import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  MdMenu, MdClose, MdArrowForward, MdPhone, MdEmail, MdLocationOn,
  MdDirections, MdCheckCircle, MdCalendarMonth, MdChevronRight,
} from 'react-icons/md';
import { FaWhatsapp, FaFacebook, FaInstagram, FaGlobe } from 'react-icons/fa';
import { getPublicClubProfile } from '../../services/clubService';
import { submitQuickEnquiry } from '../../services/trialService';
import { sanitizePhone, isValidPhone, sanitizeEmail, isValidEmail, PHONE_HINT, EMAIL_HINT } from '../../utils/inputRules';
import { useTenant } from '../../context/TenantContext';
import { TRIAL_ENABLED } from '../../config/features';
import {
  clubTypeLabel, vocab, cldImage, rgbStr, readableBrand, brandDark, isRealGps,
  ktmTodayName, ktmMinutesNow,
} from '../../utils/publicProfile';

// ── Warm Charcoal palette (this page owns its tokens — it lives outside the admin theme) ──
const BG            = '#0a0908';
const BG_CARD       = '#141210';
const BG_CARD_HOVER = '#1a1918';
const BORDER        = 'rgba(237,232,224,0.10)';
const TEXT_PRIMARY  = '#ede8e0';
const TEXT_SECONDARY = '#a09a90';
const TEXT_MUTED    = '#6f685f';
const FONT_STACK    = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

const COACH_LIMIT = 8;
const GALLERY_LIMIT = 9;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_FULL = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

// Open now? Parses "6am – 8pm" style strings; time read in Asia/Kathmandu.
function getOpenStatus(operatingHours) {
  if (!operatingHours) return null;
  const str = operatingHours?.[ktmTodayName()];
  if (!str?.trim()) return null;
  if (/closed/i.test(str.trim())) return { isOpen: false };
  const m = str.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[–\-—]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!m) return { isOpen: null };
  const toMin = (h, mn, mer) => {
    let hr = parseInt(h);
    const ms = parseInt(mn || 0);
    if (mer) {
      if (/pm/i.test(mer) && hr !== 12) hr += 12;
      if (/am/i.test(mer) && hr === 12) hr = 0;
    }
    return hr * 60 + ms;
  };
  const now = ktmMinutesNow();
  return { isOpen: now >= toMin(m[1], m[2], m[3]) && now < toMin(m[4], m[5], m[6]) };
}

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// Page-scoped CSS: keyframes, hover, responsive nav, reduced-motion — the things inline
// styles can't express. `brand` is interpolated so accents match the club.
function pageStyles(brand, brandRgb) {
  return `
  .pp-root, .pp-root * { box-sizing: border-box; }
  .pp-root ::selection { background: rgba(${brandRgb},0.3); color:#fff; }
  @keyframes pp-kenburns { from { transform: scale(1.05); } to { transform: scale(1); } }
  @keyframes pp-fadeup { from { opacity:0; transform: translateY(18px); } to { opacity:1; transform:none; } }
  @keyframes pp-spin { to { transform: rotate(360deg); } }
  .pp-kenburns { animation: pp-kenburns 9s ease-out forwards; }
  .pp-fadeup { animation: pp-fadeup .7s cubic-bezier(.2,.7,.2,1) both; }
  .pp-navlink { position:relative; padding:8px 2px; color:rgba(255,255,255,0.92); font-size:14px; font-weight:500; background:none; border:none; cursor:pointer; transition:color .18s; letter-spacing:.01em; text-shadow:0 2px 12px rgba(0,0,0,0.6); }
  .pp-navlink:hover { color:#fff; }
  .pp-navlink::after { content:''; position:absolute; left:0; right:100%; bottom:0; height:1.5px; background:${brand}; transition:right .22s ease; }
  .pp-navlink:hover::after { right:0; }
  .pp-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; font-weight:600; cursor:pointer; text-decoration:none; transition:transform .14s ease, background .18s ease, border-color .18s ease, opacity .18s; border:1px solid transparent; }
  .pp-btn:active { transform: scale(.98); }
  .pp-card { transition: border-color .22s ease, background .22s ease, transform .22s ease; }
  .pp-card:hover { border-color: rgba(${brandRgb},0.35); background:${BG_CARD_HOVER}; }
  .pp-coach-img { transition: transform .5s ease; }
  .pp-coach:hover .pp-coach-img { transform: scale(1.05); }
  .pp-gitem { overflow:hidden; }
  .pp-gitem img { transition: transform .5s ease; }
  .pp-gitem:hover img { transform: scale(1.06); }
  .pp-desktop-nav { display:flex; }
  .pp-hamburger { display:none; }
  @media (max-width:820px) {
    .pp-desktop-nav { display:none; }
    .pp-hamburger { display:inline-flex; }
  }
  @media (prefers-reduced-motion: reduce) {
    .pp-kenburns, .pp-fadeup { animation: none !important; transform: none !important; opacity:1 !important; }
    html { scroll-behavior: auto; }
  }
  `;
}

export default function PublicClubProfile() {
  const { clubSlug: urlSlug } = useParams();
  const { slug: tenantSlug } = useTenant();
  const clubSlug = urlSlug || tenantSlug || null;

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [showAllCoaches, setShowAllCoaches] = useState(false);
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [trialModalOpen, setTrialModalOpen] = useState(false);

  // Phase 0 freshness: fetch via React Query with zero stale time + always-refetch, so the live
  // page reflects the latest editor save the moment a visitor loads (or returns to) it. The
  // endpoint also sends Cache-Control:no-cache so proxies / in-app browsers can't serve stale.
  const { data: profileData, isLoading, isError, error } = useQuery({
    queryKey: ['public-club', clubSlug],
    queryFn: () => getPublicClubProfile(clubSlug).then(r => r.data),
    enabled: !!clubSlug,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: (count, err) => ![403, 404].includes(err?.response?.status) && count < 2,
  });

  const notFound = isError && [403, 404].includes(error?.response?.status);

  const club     = profileData?.club || null;
  const trainers = profileData?.trainers || [];
  const stats    = profileData?.stats || {};

  const brand    = club?.primaryColor || '#E24B4A';
  const brandRgb = rgbStr(brand);
  const brandRead = useMemo(() => readableBrand(brand), [brand]);
  const words    = useMemo(() => vocab(club?.clubType), [club?.clubType]);
  const typeLabel = clubTypeLabel(club?.clubType);

  // Nav shadow on scroll past the hero.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Title + social meta (Phase 4). Client-set — updates title/description reliably; og:image is
  // a sized Cloudinary render, cache-busted by updatedAt so a new cover re-unfurls.
  useEffect(() => {
    if (!club) return;
    const cityText = club.address?.city || club.address?.district || '';
    const fallback = cityText ? `${club.name} — ${cityText}` : club.name;
    const rawDesc = (club.tagline || club.description || fallback || '').trim();
    const description = rawDesc.length > 160 ? `${rawDesc.slice(0, 157).trimEnd()}…` : rawDesc;
    // Browser tab uses the short display name when set; link-preview og:title keeps the full
    // name (more descriptive when shared).
    const menuName = club.displayName || club.name;
    document.title = typeLabel ? `${menuName} · ${typeLabel}` : menuName;
    const title = typeLabel ? `${club.name} · ${typeLabel}` : club.name;
    let ogImg = club.coverPhotoUrl
      ? cldImage(club.coverPhotoUrl, { w: 1200, h: 630, crop: 'fill', gravity: 'auto' })
      : (club.logo || '');
    // Cache-bust so a new cover re-unfurls on WhatsApp/Facebook instead of showing the old image.
    if (ogImg && club.updatedAt) {
      const v = new Date(club.updatedAt).getTime();
      if (v) ogImg += `${ogImg.includes('?') ? '&' : '?'}v=${v}`;
    }

    const desired = [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      ...(ogImg ? [{ property: 'og:image', content: ogImg }, { name: 'twitter:image', content: ogImg }] : []),
    ];
    const restore = desired.map(({ name, property, content }) => {
      const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      const existing = document.head.querySelector(selector);
      if (existing) {
        const prev = existing.getAttribute('content');
        existing.setAttribute('content', content);
        return () => (prev == null ? existing.removeAttribute('content') : existing.setAttribute('content', prev));
      }
      const el = document.createElement('meta');
      if (name) el.setAttribute('name', name);
      if (property) el.setAttribute('property', property);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      return () => el.remove();
    });
    return () => restore.forEach(fn => fn());
  }, [club, typeLabel]);

  // Lock scroll behind lightbox / trial modal / mobile menu.
  useEffect(() => {
    const lock = lightbox || trialModalOpen || menuOpen;
    document.body.style.overflow = lock ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightbox, trialModalOpen, menuOpen]);

  if (isLoading || (!profileData && !notFound)) {
    return (
      <div style={{ minHeight: '100svh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 34, height: 34, border: '3px solid rgba(237,232,224,0.15)', borderTopColor: brandRead, borderRadius: '50%', animation: 'pp-spin .8s linear infinite' }} />
        <style>{`@keyframes pp-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (notFound || !club) {
    return (
      <div style={{ minHeight: '100svh', background: BG, color: TEXT_PRIMARY, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24, fontFamily: FONT_STACK }}>
        <MdLocationOn style={{ fontSize: 48, color: TEXT_MUTED, marginBottom: 16 }} />
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Club not found</h1>
        <p style={{ color: TEXT_SECONDARY, fontSize: 15, maxWidth: 340 }}>
          This club page isn’t available. Please check the link or contact the club directly.
        </p>
        <a href="https://trainaxis.app" style={{ marginTop: 24, color: TEXT_MUTED, fontSize: 13, textDecoration: 'none' }}>Powered by TrainAxis</a>
      </div>
    );
  }

  // ── Derived content flags (sections render only with real content) ──────────
  // Menu / small-space name: the club's short Display Name when set, else the full name
  // (truncated only if still too long for the nav lockup).
  const menuName = club.displayName || club.name;
  const shortName = menuName.length > 22 ? `${menuName.slice(0, 20).trimEnd()}…` : menuName;

  // Hero keeps the FULL name. Long names (>30 chars) step the type down one notch so they sit
  // in 2–3 graceful lines, and may break naturally after "|" / "-" separators (a <wbr> break
  // opportunity), so "Next-Gen | Master Sonam Tae Kwon Do" wraps cleanly instead of overflowing.
  const heroLong = club.name.length > 30;
  const heroFont = heroLong ? 'clamp(1.6rem,5.4vw,2.4rem)' : 'clamp(1.9rem,6.5vw,2.9rem)';
  const SEP_RE = /([|–—-])/;
  const heroNameNodes = club.name.split(SEP_RE).map((seg, i) =>
    SEP_RE.test(seg) ? <span key={i}>{seg}<wbr /></span> : <span key={i}>{seg}</span>
  );
  const cityText = club.address?.city || club.address?.district || '';
  const kicker = [typeLabel, cityText].filter(Boolean).join(' · ').toUpperCase();

  const waNum = club.whatsappNumber?.replace(/\D/g, '');
  const cover = club.coverPhotoUrl
    ? cldImage(club.coverPhotoUrl, { w: 1800, crop: 'limit' })
    : null;

  const hasHours = DAYS.some(d => (club.operatingHours?.[d] || '').trim());
  // Only show the map for a genuinely-set location. The Settings.gps default is central
  // Kathmandu (shared with check-in geofencing), so a club that never set coordinates would
  // otherwise render a Kathmandu pin — isRealGps() treats that default as "unset".
  const hasGps = isRealGps(club.gps);
  const addressLine = [club.address?.street, club.address?.city, club.address?.district].filter(Boolean).join(', ');
  const hasLocation = hasGps || !!addressLine;
  // Get Directions. With real GPS → directions straight to the exact pin. Without GPS → still
  // the DIRECTIONS endpoint (not search) with a name-qualified destination string, so Google
  // resolves the single best match and starts navigation instead of dumping a results list of
  // unrelated bus stops / shops. Leading with the club name sharpens the match.
  const directionsUrl = hasGps
    ? `https://www.google.com/maps/dir/?api=1&destination=${club.gps.lat},${club.gps.lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([club.name, addressLine, club.address?.country || 'Nepal'].filter(Boolean).join(', '))}`;
  const hasContact = !!(club.contactPhone || club.contactEmail || waNum);
  const socials = [
    club.facebookUrl && { href: club.facebookUrl, Icon: FaFacebook, label: 'Facebook' },
    club.instagramUrl && { href: club.instagramUrl, Icon: FaInstagram, label: 'Instagram' },
    club.websiteUrl && { href: club.websiteUrl, Icon: FaGlobe, label: 'Website' },
  ].filter(Boolean);

  const visibleCoaches = showAllCoaches ? trainers : trainers.slice(0, COACH_LIMIT);
  const gallery = club.galleryPhotos || [];
  const visibleGallery = showAllGallery ? gallery : gallery.slice(0, GALLERY_LIMIT);

  const navItems = [
    club.description && { id: 'about', label: 'About' },
    trainers.length > 0 && { id: 'coaches', label: words.coachPlural },
    hasLocation && { id: 'location', label: 'Location' },
    { id: 'contact', label: 'Contact' },
  ].filter(Boolean);

  // ── Stats: never a zero. Only values > 0 qualify. ───────────────────────────
  const numericStats = [
    stats.activeStudentCount > 0 && { value: stats.activeStudentCount, label: words.memberPlural },
    stats.classCount > 0 && { value: stats.classCount, label: words.sessionPlural },
    stats.yearsActive > 0 && { value: `${stats.yearsActive}+`, label: 'Years' },
  ].filter(Boolean);

  // Fallback qualitative chips so we always say something true, never "0 members".
  const trainingDays = DAYS.filter(d => {
    const v = (club.operatingHours?.[d] || '').trim();
    return v && !/closed/i.test(v);
  }).length;
  // Complementary facts only — the About card already shows the type + Est. year chips, so we
  // deliberately exclude those here to avoid the duplicate chip rows.
  const qualitativeChips = [
    trainers.length > 0 && `${trainers.length} ${trainers.length === 1 ? words.coachPlural.replace(/s$/, '') : words.coachPlural}`,
    trainingDays > 0 && `Open ${trainingDays} day${trainingDays === 1 ? '' : 's'} a week`,
  ].filter(Boolean);
  const showStats = numericStats.length > 0 || qualitativeChips.length > 0;

  const openStatus = getOpenStatus(club.operatingHours);

  return (
    <div className="pp-root" style={{ background: BG, color: TEXT_PRIMARY, fontFamily: FONT_STACK, minHeight: '100svh', WebkitFontSmoothing: 'antialiased' }}>
      <style>{pageStyles(brandRead, brandRgb)}</style>

      {/* Permanent top scrim so nav text reads over ANY cover (incl. pure white). Fades out
          once the solid nav takes over on scroll. */}
      <div aria-hidden style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 120, zIndex: 49, pointerEvents: 'none',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.28) 45%, transparent 100%)',
        opacity: scrolled ? 0 : 1, transition: 'opacity .3s ease',
      }} />

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px clamp(16px,4vw,32px)',
        background: scrolled || menuOpen ? 'rgba(10,9,8,0.92)' : 'transparent',
        borderBottom: `1px solid ${scrolled ? BORDER : 'transparent'}`,
        backdropFilter: scrolled || menuOpen ? 'blur(12px)' : 'none',
        transition: 'background .3s ease, border-color .3s ease',
      }}>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {club.logo
            ? <img src={cldImage(club.logo, { w: 72, h: 72, crop: 'fill' })} alt={club.name} width={34} height={34} style={{ width: 34, height: 34, borderRadius: 9, objectFit: 'cover' }} />
            : <div style={{ width: 34, height: 34, borderRadius: 9, background: brand, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 15 }}>{club.name[0]}</div>}
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>{shortName}</span>
        </button>

        <div className="pp-desktop-nav" style={{ alignItems: 'center', gap: 22 }}>
          {navItems.map(item => (
            <button key={item.id} className="pp-navlink" onClick={() => scrollToId(item.id)}>{item.label}</button>
          ))}
          <button className="pp-btn" onClick={() => scrollToId('contact')}
            style={{ padding: '9px 18px', borderRadius: 999, background: brand, color: '#fff', fontSize: 14 }}>
            {words.joinCta}
          </button>
        </div>

        <button className="pp-hamburger" onClick={() => setMenuOpen(o => !o)}
          style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center', background: 'rgba(237,232,224,0.06)', border: `1px solid ${BORDER}`, borderRadius: 10, color: TEXT_PRIMARY, cursor: 'pointer' }}>
          {menuOpen ? <MdClose size={22} /> : <MdMenu size={22} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ position: 'fixed', top: 66, left: 0, right: 0, zIndex: 49, background: 'rgba(10,9,8,0.98)', borderBottom: `1px solid ${BORDER}`, backdropFilter: 'blur(12px)', padding: '8px 16px 16px' }}>
          {navItems.map(item => (
            <button key={item.id} onClick={() => { scrollToId(item.id); setMenuOpen(false); }}
              style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', padding: '14px 8px', background: 'none', border: 'none', borderBottom: `1px solid ${BORDER}`, color: TEXT_PRIMARY, fontSize: 16, fontWeight: 500, cursor: 'pointer', minHeight: 44 }}>
              {item.label} <MdChevronRight style={{ color: TEXT_MUTED }} />
            </button>
          ))}
          <button className="pp-btn" onClick={() => { scrollToId('contact'); setMenuOpen(false); }}
            style={{ width: '100%', marginTop: 14, padding: '13px', borderRadius: 12, background: brand, color: '#fff', fontSize: 15 }}>
            {words.joinCta}
          </button>
        </div>
      )}

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <header style={{ position: 'relative', height: '100svh', minHeight: 560, width: '100%', overflow: 'hidden' }}>
        {cover ? (
          <img src={cover} alt={`${club.name} cover`} fetchpriority="high" decoding="async"
            className="pp-kenburns"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 28%' }} />
        ) : (
          <div className="pp-kenburns" style={{
            position: 'absolute', inset: 0,
            background: `radial-gradient(120% 90% at 50% 0%, ${brandDark(brand, 0.55)} 0%, ${brandDark(brand, 0.82)} 38%, ${BG} 78%)`,
          }} />
        )}
        {/* full-image scrim so white text reads even over a bright/white cover photo */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
        {/* bottom fade — strengthened so it works on light covers: darkens early, hits 95% by the
            bottom third. All text lives inside this zone. */}
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, transparent 0%, rgba(10,9,8,0.12) 26%, rgba(10,9,8,0.5) 48%, rgba(10,9,8,0.85) 62%, rgba(10,9,8,0.97) 74%, ${BG} 90%)` }} />

        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '0 clamp(20px,5vw,48px) clamp(40px,7vh,72px)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
            {kicker && (
              <p className="pp-fadeup" style={{ color: brandRead, fontSize: 'clamp(11px,2.6vw,13px)', fontWeight: 700, letterSpacing: '0.22em', margin: '0 0 14px', animationDelay: '.05s' }}>
                {kicker}
              </p>
            )}
            <h1 className="pp-fadeup" style={{
              color: '#fff', fontSize: heroFont, fontWeight: 800, lineHeight: 1.08,
              letterSpacing: '-0.025em', margin: 0, maxWidth: '20ch', overflowWrap: 'break-word',
              textShadow: '0 2px 12px rgba(0,0,0,0.6)',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              animationDelay: '.12s',
            }}>
              {heroNameNodes}
            </h1>
            {club.tagline && (
              <p className="pp-fadeup" style={{ color: TEXT_SECONDARY, fontSize: 'clamp(15px,3.4vw,19px)', lineHeight: 1.5, margin: '16px 0 0', maxWidth: '46ch', animationDelay: '.2s' }}>
                {club.tagline}
              </p>
            )}
            <div className="pp-fadeup" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 28, animationDelay: '.28s' }}>
              <button className="pp-btn" onClick={() => scrollToId('contact')}
                style={{ padding: '15px 28px', borderRadius: 999, background: brand, color: '#fff', fontSize: 16, minHeight: 44 }}>
                {words.joinCta} <MdArrowForward />
              </button>
              {waNum && (
                <a className="pp-btn" href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer"
                  style={{ padding: '15px 26px', borderRadius: 999, background: 'rgba(237,232,224,0.08)', border: `1px solid ${BORDER}`, color: TEXT_PRIMARY, fontSize: 16, minHeight: 44, backdropFilter: 'blur(8px)' }}>
                  <FaWhatsapp style={{ color: '#4ade80' }} /> WhatsApp
                </a>
              )}
            </div>
            {openStatus?.isOpen != null && (
              <p style={{ marginTop: 22, fontSize: 13, color: TEXT_SECONDARY, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: openStatus.isOpen ? '#4ade80' : TEXT_MUTED, boxShadow: openStatus.isOpen ? '0 0 8px #4ade80' : 'none' }} />
                {openStatus.isOpen ? 'Open now' : 'Closed now'}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* ── ABOUT ───────────────────────────────────────────────────────────── */}
      {club.description && (
        <Section id="about">
          <SectionTitle brand={brandRead}>About</SectionTitle>
          <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 'clamp(20px,3.5vw,30px)' }}>
            <p style={{ color: TEXT_SECONDARY, fontSize: 'clamp(15px,2.4vw,17px)', lineHeight: 1.75, maxWidth: '65ch', margin: 0 }}>
              {club.description}
            </p>
            {(typeLabel || club.yearEstablished) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 20 }}>
                {typeLabel && <Chip brand={brandRead} brandRgb={brandRgb}>{typeLabel}</Chip>}
                {club.yearEstablished && <Chip brand={brandRead} brandRgb={brandRgb}>Est. {club.yearEstablished}</Chip>}
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── STATS ───────────────────────────────────────────────────────────── */}
      {showStats && (
        <Section>
          {numericStats.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(numericStats.length, 3)}, 1fr)`, gap: 'clamp(10px,2vw,18px)' }}>
              {numericStats.map((s, i) => (
                <div key={i} className="pp-card" style={{ textAlign: 'center', padding: 'clamp(22px,4vw,34px) 12px', borderRadius: 14, background: BG_CARD, border: `1px solid ${BORDER}` }}>
                  <p style={{ fontSize: 'clamp(1.8rem,5vw,2.6rem)', fontWeight: 800, color: brandRead, margin: '0 0 6px', letterSpacing: '-0.02em' }}>{s.value}</p>
                  <p style={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{s.label}</p>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              {qualitativeChips.map((c, i) => <Chip key={i} brand={brandRead} brandRgb={brandRgb} large>{c}</Chip>)}
            </div>
          )}
        </Section>
      )}

      {/* ── COACHES ─────────────────────────────────────────────────────────── */}
      {trainers.length > 0 && (
        <Section id="coaches">
          <SectionTitle brand={brandRead}>Our {words.coachPlural}</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%,220px), 1fr))', gap: 'clamp(12px,2vw,20px)' }}>
            {visibleCoaches.map(t => <CoachCard key={t._id} t={t} brand={brand} accent={brandRead} brandRgb={brandRgb} />)}
          </div>
          {trainers.length > COACH_LIMIT && !showAllCoaches && (
            <MoreButton brand={brandRead} onClick={() => setShowAllCoaches(true)}>
              Show all {trainers.length} {words.coachPlural.toLowerCase()}
            </MoreButton>
          )}
        </Section>
      )}

      {/* ── GALLERY ─────────────────────────────────────────────────────────── */}
      {gallery.length > 0 && (
        <Section id="gallery">
          <SectionTitle brand={brandRead}>Gallery</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%,180px), 1fr))', gap: 'clamp(8px,1.5vw,14px)' }}>
            {visibleGallery.map((url, i) => (
              <button key={i} className="pp-gitem" onClick={() => setLightbox(url)}
                style={{ aspectRatio: '1 / 1', borderRadius: 12, border: `1px solid ${BORDER}`, padding: 0, cursor: 'pointer', background: BG_CARD }}>
                <img src={cldImage(url, { w: 500, h: 500, crop: 'fill' })} alt={`${club.name} ${i + 1}`} loading="lazy" decoding="async"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </button>
            ))}
          </div>
          {gallery.length > GALLERY_LIMIT && !showAllGallery && (
            <MoreButton brand={brandRead} onClick={() => setShowAllGallery(true)}>Show all {gallery.length} photos</MoreButton>
          )}
        </Section>
      )}

      {/* ── HOURS & LOCATION ────────────────────────────────────────────────── */}
      {(hasHours || hasLocation) && (
        <Section id="location">
          <SectionTitle brand={brandRead}>Hours &amp; Location</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,300px), 1fr))', gap: 'clamp(14px,2.5vw,24px)' }}>
            {hasHours && (
              <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 'clamp(18px,3vw,26px)' }}>
                <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: TEXT_PRIMARY, fontWeight: 700, fontSize: 15, margin: '0 0 16px' }}>
                  <MdCalendarMonth style={{ color: brandRead }} /> Opening Hours
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {DAYS.map(d => {
                      const val = (club.operatingHours?.[d] || '').trim();
                      const isToday = d === ktmTodayName();
                      return (
                        <tr key={d} style={{ background: isToday ? `rgba(${brandRgb},0.10)` : 'transparent' }}>
                          <td style={{ padding: '9px 12px', fontSize: 14, fontWeight: isToday ? 700 : 500, color: isToday ? brandRead : TEXT_SECONDARY, borderRadius: '8px 0 0 8px' }}>
                            {DAY_FULL[d]}{isToday && <span style={{ fontSize: 10, marginLeft: 6, color: brandRead }}>• Today</span>}
                          </td>
                          <td style={{ padding: '9px 12px', fontSize: 14, textAlign: 'right', color: val ? TEXT_PRIMARY : TEXT_MUTED, fontWeight: isToday ? 600 : 400, borderRadius: '0 8px 8px 0' }}>
                            {val || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {hasLocation && (
              <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 'clamp(18px,3vw,26px)', display: 'flex', flexDirection: 'column' }}>
                <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: TEXT_PRIMARY, fontWeight: 700, fontSize: 15, margin: '0 0 16px' }}>
                  <MdLocationOn style={{ color: brandRead }} /> Find Us
                </p>
                {/* Header row: address + Get Directions, above the map */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: hasGps ? 16 : 0 }}>
                  {addressLine && <p style={{ color: TEXT_SECONDARY, fontSize: 14, lineHeight: 1.55, margin: 0, flex: '1 1 160px', minWidth: 0 }}>{addressLine}</p>}
                  <a className="pp-btn" href={directionsUrl} target="_blank" rel="noreferrer"
                    style={{ padding: '11px 16px', borderRadius: 11, background: 'rgba(237,232,224,0.06)', border: `1px solid ${BORDER}`, color: TEXT_PRIMARY, fontSize: 13.5, minHeight: 44, flexShrink: 0 }}>
                    <MdDirections style={{ color: brandRead }} /> Get Directions
                  </a>
                </div>
                {hasGps && (
                  <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', border: `1px solid ${BORDER}`, height: 'clamp(200px,42vw,320px)' }}>
                    <iframe title="Map" loading="lazy" width="100%" height="100%" style={{ border: 0, display: 'block', filter: 'grayscale(0.2) invert(0.92) hue-rotate(180deg)' }}
                      src={`https://www.google.com/maps?q=${club.gps.lat},${club.gps.lng}&z=15&output=embed`} referrerPolicy="no-referrer-when-downgrade" />
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ── GET IN TOUCH (Join a class scroll target) ───────────────────────── */}
      <Section id="contact">
        <SectionTitle brand={brandRead}>Get in Touch</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%,300px), 1fr))', gap: 'clamp(14px,2.5vw,24px)' }}>
          {/* Join CTA — warm card; only the button carries brand colour */}
          <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 'clamp(22px,4vw,32px)' }}>
            <h3 style={{ color: TEXT_PRIMARY, fontSize: 'clamp(1.2rem,3.5vw,1.6rem)', fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
              Ready to start?
            </h3>
            <p style={{ color: TEXT_SECONDARY, fontSize: 15, lineHeight: 1.6, margin: '0 0 22px' }}>
              {TRIAL_ENABLED
                ? `Send your details and ${club.name} will reach out about ${words.sessionPlural.toLowerCase()} and trial availability.`
                : `Reach out to ${club.name} to learn about ${words.sessionPlural.toLowerCase()} and getting started.`}
            </p>
            {TRIAL_ENABLED ? (
              <button className="pp-btn" onClick={() => setTrialModalOpen(true)}
                style={{ width: '100%', padding: '15px', borderRadius: 12, background: brand, color: '#fff', fontSize: 16, minHeight: 44 }}>
                Book a free trial <MdArrowForward />
              </button>
            ) : waNum ? (
              <a className="pp-btn" href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer"
                style={{ width: '100%', padding: '15px', borderRadius: 12, background: brand, color: '#fff', fontSize: 16, minHeight: 44 }}>
                <FaWhatsapp /> Message on WhatsApp
              </a>
            ) : club.contactPhone && (
              <a className="pp-btn" href={`tel:${club.contactPhone}`}
                style={{ width: '100%', padding: '15px', borderRadius: 12, background: brand, color: '#fff', fontSize: 16, minHeight: 44 }}>
                <MdPhone /> Call the club
              </a>
            )}
          </div>

          {/* Contact details */}
          {hasContact && (
            <div style={{ background: BG_CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: 'clamp(18px,3vw,26px)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {club.contactPhone && <ContactRow href={`tel:${club.contactPhone}`} Icon={MdPhone} brand={brandRead} brandRgb={brandRgb} label={club.contactPhone} sub="Call" />}
              {waNum && <ContactRow href={`https://wa.me/${waNum}`} external Icon={FaWhatsapp} brand="#4ade80" brandRgb="74,222,128" label={club.whatsappNumber} sub="WhatsApp" />}
              {club.contactEmail && <ContactRow href={`mailto:${club.contactEmail}`} Icon={MdEmail} brand={brandRead} brandRgb={brandRgb} label={club.contactEmail} sub="Email" />}
            </div>
          )}
        </div>
      </Section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: `1px solid ${BORDER}`, padding: 'clamp(32px,5vw,48px) clamp(20px,5vw,48px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {club.logo && <img src={cldImage(club.logo, { w: 64, h: 64, crop: 'fill' })} alt="" width={28} height={28} style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover' }} />}
            <span style={{ color: TEXT_PRIMARY, fontWeight: 700, fontSize: 17 }}>{menuName}</span>
          </div>
          {socials.length > 0 && (
            <div style={{ display: 'flex', gap: 12 }}>
              {socials.map(({ href, Icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                  style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 11, background: 'rgba(237,232,224,0.05)', border: `1px solid ${BORDER}`, color: TEXT_SECONDARY, textDecoration: 'none' }}>
                  <Icon size={18} />
                </a>
              ))}
            </div>
          )}
          <a href="https://trainaxis.app" target="_blank" rel="noreferrer" style={{ color: TEXT_MUTED, fontSize: 13, textDecoration: 'none', marginTop: 4 }}>
            Powered by <span style={{ color: TEXT_SECONDARY, fontWeight: 600 }}>TrainAxis</span>
          </a>
        </div>
      </footer>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(5,4,3,0.94)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, cursor: 'zoom-out' }}>
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 18, right: 18, width: 44, height: 44, borderRadius: '50%', background: 'rgba(237,232,224,0.08)', border: 'none', color: TEXT_PRIMARY, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MdClose size={22} />
          </button>
          <img src={cldImage(lightbox, { w: 1400, crop: 'limit' })} alt="" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 12, objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* Trial modal (enquiry) */}
      {trialModalOpen && (
        <BookTrialModal clubSlug={club.slug || clubSlug} club={club} brand={brand} accent={brandRead} brandRgb={brandRgb} onClose={() => setTrialModalOpen(false)} />
      )}
    </div>
  );
}

// ── Shared building blocks ──────────────────────────────────────────────────────
function Section({ id, children }) {
  return (
    <section id={id} style={{ scrollMarginTop: 80, padding: 'clamp(2.5rem,6vw,3.5rem) clamp(20px,5vw,48px)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>{children}</div>
    </section>
  );
}

function SectionTitle({ children, brand }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'clamp(22px,4vw,32px)' }}>
      <span style={{ width: 26, height: 3, borderRadius: 99, background: brand }} />
      <h2 style={{ color: TEXT_PRIMARY, fontSize: 'clamp(1.35rem,4vw,1.6rem)', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>{children}</h2>
    </div>
  );
}

function Chip({ children, brand, brandRgb, large }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: large ? '10px 18px' : '7px 14px',
      borderRadius: 999, background: `rgba(${brandRgb},0.10)`, border: `1px solid rgba(${brandRgb},0.22)`,
      color: brand, fontSize: large ? 14 : 13, fontWeight: 600,
    }}>{children}</span>
  );
}

function MoreButton({ children, onClick, brand }) {
  return (
    <div style={{ textAlign: 'center', marginTop: 28 }}>
      <button className="pp-btn" onClick={onClick}
        style={{ padding: '12px 24px', borderRadius: 999, background: 'transparent', border: `1px solid ${BORDER}`, color: brand, fontSize: 14, minHeight: 44 }}>
        {children}
      </button>
    </div>
  );
}

function CoachCard({ t, brand, accent, brandRgb }) {
  const initials = (t.fullName || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="pp-coach pp-card" style={{ borderRadius: 14, overflow: 'hidden', background: BG_CARD, border: `1px solid ${BORDER}` }}>
      <div style={{ aspectRatio: '4 / 5', overflow: 'hidden', position: 'relative', background: `rgba(${brandRgb},0.08)` }}>
        {t.profilePhoto ? (
          <img className="pp-coach-img" src={cldImage(t.profilePhoto, { w: 440, h: 550, crop: 'fill', gravity: 'face' })} alt={t.fullName} loading="lazy" decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 76, height: 76, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: brand, color: '#fff', fontWeight: 800, fontSize: 26 }}>{initials}</div>
          </div>
        )}
      </div>
      <div style={{ padding: '14px 16px 16px' }}>
        <p style={{ color: TEXT_PRIMARY, fontWeight: 700, fontSize: 15, margin: '0 0 4px' }}>{t.fullName}</p>
        {t.specializations?.length > 0 && (
          <p style={{ color: accent, fontSize: 12.5, fontWeight: 600, margin: 0 }}>{t.specializations.slice(0, 2).join(' · ')}</p>
        )}
        {t.experienceYears > 0 && (
          <p style={{ color: TEXT_MUTED, fontSize: 12, margin: '4px 0 0' }}>{t.experienceYears}+ years</p>
        )}
      </div>
    </div>
  );
}

function ContactRow({ href, external, Icon, label, sub, brand, brandRgb }) {
  return (
    <a href={href} {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 8px', borderRadius: 10, textDecoration: 'none', minHeight: 44 }}>
      <span style={{ width: 42, height: 42, flexShrink: 0, borderRadius: 11, background: `rgba(${brandRgb},0.12)`, border: `1px solid rgba(${brandRgb},0.22)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: brand }}>
        <Icon size={18} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 11, color: TEXT_MUTED, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{sub}</span>
        <span style={{ display: 'block', fontSize: 15, color: TEXT_PRIMARY, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </span>
    </a>
  );
}

// ── Trial enquiry modal (functionality preserved; restyled to the page palette) ──
function BookTrialModal({ clubSlug, club, brand, accent, brandRgb, onClose }) {
  const [form, setForm] = useState({
    fullName: '', age: '', parentGuardianName: '', phone: '', email: '',
    preferredTimeSlot: '', enquiryMessage: '', website: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | duplicate | error | rateLimited
  const [serverMsg, setServerMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [errorStage, setErrorStage] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const parsedAge = parseInt(form.age, 10);
  const isMinor = !isNaN(parsedAge) && parsedAge < 18;

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Name is required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (!isValidPhone(form.phone)) e.phone = PHONE_HINT;
    if (form.email.trim() && !isValidEmail(form.email)) e.email = EMAIL_HINT;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const safeSlug = (typeof clubSlug === 'string' && clubSlug.trim() && clubSlug !== 'undefined') ? clubSlug.trim() : null;
    if (!safeSlug) { setErrorMsg('We could not identify this club. Please contact the club directly.'); setErrorCode(''); setStatus('error'); return; }

    setStatus('submitting'); setServerMsg('');
    try {
      const res = await submitQuickEnquiry(safeSlug, {
        fullName: form.fullName.trim(), age: form.age, parentGuardianName: form.parentGuardianName.trim(),
        phone: form.phone.trim(), email: form.email.trim(), preferredTimeSlot: form.preferredTimeSlot,
        enquiryMessage: form.enquiryMessage.trim(), website: form.website,
      });
      if (res.data?.duplicate) { setStatus('duplicate'); setServerMsg(res.data.message || 'A request already exists.'); }
      else setStatus('success');
    } catch (err) {
      const rid = err.response?.data?.requestId || '';
      const stage = err.response?.data?.stage || '';
      setErrorCode(rid); setErrorStage(stage);
      if (err.isFriendlySlugError) { setErrorMsg(err.message); setStatus('error'); }
      else if (err.response?.status === 429) setStatus('rateLimited');
      else if (!err.response) { setErrorMsg('We could not connect to the server. Please check your connection or contact the club directly.'); setStatus('error'); }
      else { setErrorMsg(err.response.data?.message || 'We could not submit your request right now. Please contact the club directly.'); setStatus('error'); }
    }
  };

  const waNum = club.whatsappNumber?.replace(/\D/g, '');
  const callNum = club.contactPhone;

  const inputStyle = (hasErr) => ({
    width: '100%', padding: '13px 16px', borderRadius: 12, fontSize: 15, fontFamily: FONT_STACK,
    background: 'rgba(237,232,224,0.05)', border: `1px solid ${hasErr ? 'rgba(239,68,68,0.6)' : BORDER}`,
    color: TEXT_PRIMARY, outline: 'none', boxSizing: 'border-box',
  });
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: TEXT_SECONDARY, marginBottom: 6 };

  const AltContacts = ({ showTryAgain }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {callNum && (
        <a href={`tel:${callNum}`} className="pp-btn" style={{ padding: '12px 20px', borderRadius: 12, background: `rgba(${brandRgb},0.12)`, border: `1px solid rgba(${brandRgb},0.25)`, color: accent, fontSize: 14 }}>
          <MdPhone size={18} /> Call the Club
        </a>
      )}
      {waNum && (
        <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" className="pp-btn" style={{ padding: '12px 20px', borderRadius: 12, background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80', fontSize: 14 }}>
          <FaWhatsapp size={16} /> WhatsApp the Club
        </a>
      )}
      {showTryAgain && (
        <button onClick={() => { setStatus('idle'); setErrorMsg(''); setErrorCode(''); setErrorStage(''); }}
          style={{ padding: '11px 20px', borderRadius: 12, background: 'rgba(237,232,224,0.05)', border: `1px solid ${BORDER}`, color: TEXT_MUTED, fontWeight: 500, fontSize: 14, cursor: 'pointer' }}>Try Again</button>
      )}
      <button onClick={onClose} style={{ padding: '11px 20px', borderRadius: 12, background: 'none', border: 'none', color: TEXT_MUTED, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>Back to Website</button>
    </div>
  );

  const StatusView = () => {
    if (status === 'success' || status === 'duplicate') {
      const dup = status === 'duplicate';
      return (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ width: 68, height: 68, borderRadius: '50%', background: dup ? `rgba(${brandRgb},0.12)` : 'rgba(74,222,128,0.12)', border: `2px solid ${dup ? accent : '#4ade80'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <MdCheckCircle style={{ fontSize: 34, color: dup ? accent : '#4ade80' }} />
          </div>
          <h3 style={{ color: TEXT_PRIMARY, fontWeight: 800, fontSize: 20, marginBottom: 8 }}>{dup ? 'Already Received' : 'Trial Request Sent!'}</h3>
          <p style={{ color: TEXT_SECONDARY, fontSize: 14, lineHeight: 1.7, maxWidth: 300, margin: '0 auto 28px' }}>
            {dup ? serverMsg : `Your details have been sent to ${club.name}. They will contact you about trial availability.`}
          </p>
          <AltContacts />
        </div>
      );
    }
    if (status === 'rateLimited') {
      return (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ width: 68, height: 68, borderRadius: '50%', background: 'rgba(251,191,36,0.1)', border: '2px solid rgba(251,191,36,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <MdPhone style={{ fontSize: 34, color: '#fbbf24' }} />
          </div>
          <h3 style={{ color: TEXT_PRIMARY, fontWeight: 800, fontSize: 20, marginBottom: 8 }}>Too Many Requests</h3>
          <p style={{ color: TEXT_SECONDARY, fontSize: 14, lineHeight: 1.7, maxWidth: 300, margin: '0 auto 28px' }}>We have received several requests from this device. You can still contact the club directly.</p>
          <AltContacts />
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ width: 68, height: 68, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', border: '2px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <MdPhone style={{ fontSize: 34, color: '#f87171' }} />
          </div>
          <h3 style={{ color: TEXT_PRIMARY, fontWeight: 800, fontSize: 20, marginBottom: 8 }}>Submission Failed</h3>
          <p style={{ color: TEXT_SECONDARY, fontSize: 14, lineHeight: 1.7, maxWidth: 300, margin: '0 auto 20px' }}>{errorMsg}</p>
          {errorCode && <p style={{ color: TEXT_MUTED, fontSize: 11, marginBottom: 20, fontFamily: 'monospace' }}>Support code: {errorCode}{errorStage ? ` (${errorStage})` : ''}</p>}
          <AltContacts showTryAgain />
        </div>
      );
    }
    return null;
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(5,4,3,0.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto', fontFamily: FONT_STACK }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 440, background: BG_CARD, border: `1px solid rgba(${brandRgb},0.2)`, borderRadius: 20, padding: 28, position: 'relative', maxHeight: '92vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, width: 44, height: 44, borderRadius: '50%', background: 'rgba(237,232,224,0.06)', border: 'none', color: TEXT_SECONDARY, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MdClose size={20} />
        </button>

        <StatusView />

        {(status === 'idle' || status === 'submitting') && (
          <>
            <div style={{ marginBottom: 24, paddingRight: 40 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                {club.logo && <img src={cldImage(club.logo, { w: 64, h: 64, crop: 'fill' })} alt={club.name} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover' }} />}
                <h3 style={{ color: TEXT_PRIMARY, fontWeight: 800, fontSize: 18, margin: 0 }}>Book a Free Trial</h3>
              </div>
              <p style={{ color: TEXT_MUTED, fontSize: 13, lineHeight: 1.5 }}>
                Your details go directly to <strong style={{ color: TEXT_SECONDARY }}>{club.name}</strong>. They will contact you about trial availability.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <input type="text" tabIndex={-1} aria-hidden="true" autoComplete="off" style={{ position: 'absolute', left: -9999, opacity: 0, width: 1, height: 1 }} value={form.website} onChange={e => set('website', e.target.value)} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={labelStyle}>Full Name <span style={{ color: '#f87171' }}>*</span></label>
                  <input type="text" placeholder="e.g. Anusha Dhakal" autoComplete="name" value={form.fullName} onChange={e => set('fullName', e.target.value)} style={inputStyle(errors.fullName)} />
                  {errors.fullName && <p style={{ color: '#f87171', fontSize: 12, marginTop: 4 }}>{errors.fullName}</p>}
                </div>
                <div>
                  <label style={labelStyle}>Age <span style={{ color: TEXT_MUTED, fontWeight: 400 }}>(optional)</span></label>
                  <input type="number" inputMode="numeric" placeholder="e.g. 14" min="1" max="100" value={form.age} onChange={e => set('age', e.target.value)} style={inputStyle(false)} />
                </div>
                {isMinor && (
                  <div>
                    <label style={labelStyle}>Parent / Guardian Name</label>
                    <input type="text" placeholder="Required for minors" autoComplete="off" value={form.parentGuardianName} onChange={e => set('parentGuardianName', e.target.value)} style={inputStyle(false)} />
                  </div>
                )}
                <div>
                  <label style={labelStyle}>Phone Number <span style={{ color: '#f87171' }}>*</span></label>
                  <input type="tel" inputMode="numeric" placeholder="97XXXXXXXX" autoComplete="tel" value={form.phone} onChange={e => set('phone', sanitizePhone(e.target.value))} style={inputStyle(errors.phone)} />
                  {errors.phone && <p style={{ color: '#f87171', fontSize: 12, marginTop: 4 }}>{errors.phone}</p>}
                </div>
                <div>
                  <label style={labelStyle}>Email <span style={{ color: TEXT_MUTED, fontWeight: 400 }}>(optional)</span></label>
                  <input type="email" inputMode="email" placeholder="you@example.com" autoComplete="email" value={form.email} onChange={e => set('email', sanitizeEmail(e.target.value))} style={inputStyle(errors.email)} />
                  {errors.email && <p style={{ color: '#f87171', fontSize: 12, marginTop: 4 }}>{errors.email}</p>}
                </div>
                <div>
                  <label style={labelStyle}>Preferred Time <span style={{ color: TEXT_MUTED, fontWeight: 400 }}>(optional)</span></label>
                  <select value={form.preferredTimeSlot} onChange={e => set('preferredTimeSlot', e.target.value)} style={{ ...inputStyle(false), appearance: 'none' }}>
                    <option value="">No preference</option>
                    <option value="Morning">Morning</option>
                    <option value="Afternoon">Afternoon</option>
                    <option value="Evening">Evening</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Message <span style={{ color: TEXT_MUTED, fontWeight: 400 }}>(optional)</span></label>
                  <textarea rows={3} placeholder="Any questions or details…" value={form.enquiryMessage} onChange={e => set('enquiryMessage', e.target.value)} style={{ ...inputStyle(false), resize: 'none' }} />
                </div>
                <button type="submit" disabled={status === 'submitting'} className="pp-btn"
                  style={{ padding: '14px 24px', borderRadius: 14, background: status === 'submitting' ? `rgba(${brandRgb},0.5)` : brand, color: '#fff', fontWeight: 700, fontSize: 15, cursor: status === 'submitting' ? 'not-allowed' : 'pointer', minHeight: 44 }}>
                  {status === 'submitting'
                    ? <><span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'pp-spin 0.7s linear infinite', flexShrink: 0 }} /> Submitting…</>
                    : <>Submit Trial Request <MdArrowForward /></>}
                </button>
                <p style={{ color: TEXT_MUTED, fontSize: 12, textAlign: 'center', lineHeight: 1.5 }}>The club will contact you soon. Nothing is auto-sent on your behalf.</p>
              </div>
            </form>
          </>
        )}
        <style>{`@keyframes pp-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );
}
